/**
 * Shared currency utilities for Argalis V2.
 *
 * Single source of truth for the 27 supported ISO 4217 currencies.
 * Used by: entity settings (Finance tab), pricing grids, articles, expenses,
 * logistics, dashboard, and all future modules dealing with monetary amounts.
 *
 * Pattern Money (Pilier II): every amount is a pair (amount, currency).
 * Display uses Intl.NumberFormat which natively handles decimal places per currency.
 */

import { coerceToNumber } from '@/lib/coerceToNumber'

export interface CurrencyInfo {
    /** ISO 4217 code (3 uppercase letters) */
    code: string
    /** French display name */
    labelFr: string
    /** English display name */
    labelEn: string
    /** Display symbol (€, $, F CFA, etc.) */
    symbol: string
    /** Number of decimal places (0, 2, or 3) */
    decimals: number
    /** Region group for Select component grouping */
    region: 'europe' | 'maghreb' | 'west_africa' | 'central_africa' | 'east_africa' | 'caribbean' | 'pacific' | 'world'
}

// ── 27 supported currencies ─────────────────────────────────────────

export const SUPPORTED_CURRENCIES: readonly CurrencyInfo[] = [
    // Europe & North America
    { code: 'EUR', labelFr: 'Euro', labelEn: 'Euro', symbol: '€', decimals: 2, region: 'europe' },
    { code: 'CHF', labelFr: 'Franc suisse', labelEn: 'Swiss franc', symbol: 'CHF', decimals: 2, region: 'europe' },
    { code: 'GBP', labelFr: 'Livre sterling', labelEn: 'British pound', symbol: '£', decimals: 2, region: 'europe' },
    { code: 'USD', labelFr: 'Dollar américain', labelEn: 'US dollar', symbol: '$', decimals: 2, region: 'europe' },
    { code: 'CAD', labelFr: 'Dollar canadien', labelEn: 'Canadian dollar', symbol: 'CA$', decimals: 2, region: 'europe' },
    // Maghreb
    { code: 'MAD', labelFr: 'Dirham marocain', labelEn: 'Moroccan dirham', symbol: 'MAD', decimals: 2, region: 'maghreb' },
    { code: 'TND', labelFr: 'Dinar tunisien', labelEn: 'Tunisian dinar', symbol: 'TND', decimals: 3, region: 'maghreb' },
    { code: 'DZD', labelFr: 'Dinar algérien', labelEn: 'Algerian dinar', symbol: 'DZD', decimals: 2, region: 'maghreb' },
    { code: 'MRU', labelFr: 'Ouguiya mauritanien', labelEn: 'Mauritanian ouguiya', symbol: 'MRU', decimals: 2, region: 'maghreb' },
    // West Africa
    { code: 'XOF', labelFr: 'Franc CFA (BCEAO)', labelEn: 'CFA franc (BCEAO)', symbol: 'F CFA', decimals: 0, region: 'west_africa' },
    { code: 'GNF', labelFr: 'Franc guinéen', labelEn: 'Guinean franc', symbol: 'GNF', decimals: 0, region: 'west_africa' },
    // Central Africa
    { code: 'XAF', labelFr: 'Franc CFA (BEAC)', labelEn: 'CFA franc (BEAC)', symbol: 'F CFA', decimals: 0, region: 'central_africa' },
    { code: 'CDF', labelFr: 'Franc congolais', labelEn: 'Congolese franc', symbol: 'CDF', decimals: 2, region: 'central_africa' },
    // East Africa & Indian Ocean
    { code: 'BIF', labelFr: 'Franc burundais', labelEn: 'Burundian franc', symbol: 'BIF', decimals: 0, region: 'east_africa' },
    { code: 'RWF', labelFr: 'Franc rwandais', labelEn: 'Rwandan franc', symbol: 'RWF', decimals: 0, region: 'east_africa' },
    { code: 'KMF', labelFr: 'Franc comorien', labelEn: 'Comorian franc', symbol: 'KMF', decimals: 0, region: 'east_africa' },
    { code: 'DJF', labelFr: 'Franc djiboutien', labelEn: 'Djiboutian franc', symbol: 'DJF', decimals: 0, region: 'east_africa' },
    { code: 'MGA', labelFr: 'Ariary malgache', labelEn: 'Malagasy ariary', symbol: 'Ar', decimals: 2, region: 'east_africa' },
    { code: 'MUR', labelFr: 'Roupie mauricienne', labelEn: 'Mauritian rupee', symbol: '₨', decimals: 2, region: 'east_africa' },
    { code: 'SCR', labelFr: 'Roupie seychelloise', labelEn: 'Seychellois rupee', symbol: '₨', decimals: 2, region: 'east_africa' },
    // Caribbean
    { code: 'HTG', labelFr: 'Gourde haïtienne', labelEn: 'Haitian gourde', symbol: 'HTG', decimals: 2, region: 'caribbean' },
    { code: 'XCD', labelFr: 'Dollar Caraïbes orientales', labelEn: 'East Caribbean dollar', symbol: 'EC$', decimals: 2, region: 'caribbean' },
    // Pacific
    { code: 'XPF', labelFr: 'Franc CFP', labelEn: 'CFP franc', symbol: 'F CFP', decimals: 0, region: 'pacific' },
    { code: 'VUV', labelFr: 'Vatu vanuatais', labelEn: 'Vanuatu vatu', symbol: 'VT', decimals: 0, region: 'pacific' },
    // Other major currencies
    { code: 'JPY', labelFr: 'Yen japonais', labelEn: 'Japanese yen', symbol: '¥', decimals: 0, region: 'world' },
    { code: 'CNY', labelFr: 'Yuan renminbi', labelEn: 'Chinese yuan', symbol: '¥', decimals: 2, region: 'world' },
    { code: 'AUD', labelFr: 'Dollar australien', labelEn: 'Australian dollar', symbol: 'A$', decimals: 2, region: 'world' },
] as const

/** Just the ISO codes, for quick iteration or Select values. */
export const CURRENCY_CODES = SUPPORTED_CURRENCIES.map(c => c.code)

// ── Lookup maps (built once) ────────────────────────────────────────

const _byCode = new Map(SUPPORTED_CURRENCIES.map(c => [c.code, c]))

/** Get the display symbol for a currency code. Falls back to the code itself. */
export function getCurrencySymbol(code: string): string {
    return _byCode.get(code)?.symbol ?? code
}

/** Get the number of decimal places for a currency. Defaults to 2. */
export function getCurrencyDecimals(code: string): number {
    return _byCode.get(code)?.decimals ?? 2
}

/** Get full CurrencyInfo for a code, or undefined if unsupported. */
export function getCurrencyInfo(code: string): CurrencyInfo | undefined {
    return _byCode.get(code)
}

/**
 * Format a numeric amount with its currency, respecting locale and currency decimals.
 *
 * Intl.NumberFormat natively handles decimal places per ISO 4217 code:
 * - formatCurrency(350, 'EUR', 'fr') → "350,00 €"
 * - formatCurrency(350, 'XOF', 'fr') → "350 F CFA"
 * - formatCurrency(350, 'JPY', 'ja') → "¥350"
 */
/**
 * 🔴 **Le repli `'fr-FR'` figeait le français chez 39 appelants du seul module
 * Devis**, sur 86 — l'autre moitié passait `i18n.language`. Résultat : sur la
 * MÊME bande de tête d'une fiche, le total HT s'affichait « 1 200,00 € » et la
 * commission « €240.00 », à six centimètres l'un de l'autre. Corriger les 39
 * sites laissait le 40ᵉ arriver ; le repli devait suivre la langue ACTIVE.
 *
 * La langue se lit sur `document.documentElement.lang`, que `useLanguage` tient
 * déjà à jour à chaque changement — et NON par un `import` de l'instance i18n :
 * importer i18n dans une fonction feuille tire toute la chaîne d'initialisation
 * dans le moindre test qui mocke `react-i18next`, et fait rougir des fichiers
 * qui n'ont rien à voir avec la devise (constaté sur `ObjectiveGauges.test`).
 * Repli `'fr-FR'` avant le premier rendu.
 */
export function localeActive(locale?: string): string {
    if (locale) return locale
    const attr = typeof document !== 'undefined' ? document.documentElement.lang : ''
    return attr || 'fr-FR'
}

/**
 * Formatteurs MÉMOÏSÉS par (locale, devise).
 *
 * Construire un `Intl.NumberFormat` coûte ~72 fois plus cher que formater avec
 * un formatteur existant (mesuré : 900 constructions+formats = 30,3 ms, contre
 * 0,42 ms à formatteur hissé). Un kanban de 12 colonnes × 25 tuiles en
 * construisait un par tuile, et `QuoteKanbanTile` ne porte aucun `React.memo` :
 * chaque survol de glisser-déposer repayait la facture — ~54 ms de fil
 * principal bloquant par rendu sur un poste de bureau, ~160 ms sur une machine
 * milieu de gamme. Le budget INP de 200 ms était franchi pendant le geste
 * central de l'écran. Le socle `data-table/cell-renderers` hisse déjà les siens.
 */
const _formatteurs = new Map<string, Intl.NumberFormat>()

/**
 * Formatteurs de NOMBRE mémoïsés par (langue, options) — jumeau de
 * `_formatteurs`, pour les mêmes raisons : construire un `Intl.NumberFormat`
 * coûte ~72 fois plus cher que formater avec un existant, et une table en
 * construisait un par cellule.
 *
 * Vit ici, à côté de `localeActive`, pour qu'il n'existe qu'UN endroit où la
 * langue active gouverne le formatage d'un chiffre.
 */
const _formatteursNombre = new Map<string, Intl.NumberFormat>()

export function getNumberFormatter(
    locale: string | undefined,
    options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
    const lang = localeActive(locale)
    const cle = `${lang}|${JSON.stringify(options)}`
    let fmt = _formatteursNombre.get(cle)
    if (!fmt) {
        fmt = new Intl.NumberFormat(lang, options)
        _formatteursNombre.set(cle, fmt)
    }
    return fmt
}

export function formatCurrency(value: number, currencyCode: string, locale?: string): string {
    const lang = localeActive(locale)
    const cle = `${lang}|${currencyCode}`
    let fmt = _formatteurs.get(cle)
    if (!fmt) {
        try {
            fmt = new Intl.NumberFormat(lang, { style: 'currency', currency: currencyCode })
        } catch {
            // Intl ne connaît pas cette devise — repli non mémoïsé.
            return `${value} ${currencyCode}`
        }
        _formatteurs.set(cle, fmt)
    }
    return fmt.format(value)
}

/**
 * Format a NULLABLE Decimal-as-string amount (API Money pattern), returning
 * `undefined` when the amount is absent or non-finite — so callers can omit the
 * row entirely rather than render an ambiguous "—" (e.g. a permission-gated rate).
 * Coerces the Decimal string once, defaults the currency to EUR.
 */
export function formatMoneyMaybe(
    amount: number | string | null | undefined,
    currencyCode: string | null | undefined,
    locale?: string,
): string | undefined {
    const n = coerceToNumber(amount)
    if (n == null) return undefined
    return formatCurrency(n, currencyCode || 'EUR', locale)
}

// ── Region labels for grouped Select ────────────────────────────────

export const CURRENCY_REGION_LABELS: Record<CurrencyInfo['region'], { fr: string; en: string }> = {
    europe: { fr: 'Europe & Amérique du Nord', en: 'Europe & North America' },
    maghreb: { fr: 'Maghreb', en: 'Maghreb' },
    west_africa: { fr: 'Afrique de l\'Ouest', en: 'West Africa' },
    central_africa: { fr: 'Afrique centrale', en: 'Central Africa' },
    east_africa: { fr: 'Afrique de l\'Est & Océan Indien', en: 'East Africa & Indian Ocean' },
    caribbean: { fr: 'Caraïbes', en: 'Caribbean' },
    pacific: { fr: 'Pacifique', en: 'Pacific' },
    world: { fr: 'Autres devises', en: 'Other currencies' },
}

/** Group currencies by region (for Select component with optgroups). */
export function getCurrenciesByRegion(): Map<CurrencyInfo['region'], CurrencyInfo[]> {
    const groups = new Map<CurrencyInfo['region'], CurrencyInfo[]>()
    for (const c of SUPPORTED_CURRENCIES) {
        const list = groups.get(c.region) ?? []
        list.push(c)
        groups.set(c.region, list)
    }
    return groups
}
